import { customElement, property } from 'lit/decorators.js';
import { CSSResult, html, LitElement, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { SizeWithStores, Store } from '../../@types/online-reservation.js';
import { sharedCss } from '../../scripts/style/shared.js';
import { trackClickAndReserveEvent } from '../tracking/trackingLayer.js';
import styles from './online-reservation-link.scss?inline';

const MAX_NEAREST_STORES = 3;

@customElement('ents-online-reservation-link')
export class OnlineReservationLink extends LitElement {
  @property({ type: String, attribute: 'article-id' })
  articleId = '';

  @property({ type: Boolean, attribute: 'is-size-selected' })
  isSizeSelected = false;

  @property({ type: String, attribute: 'default-message' })
  defaultMessage = '';

  @property({ type: String, attribute: 'stores-message' })
  storesMessage = '';

  @property({ type: String, attribute: 'model-url-template' })
  modelUrlTemplate = '';

  @property({ type: Array, attribute: 'nearest-store-ids' })
  nearestStoreIds: number[] = [];

  private readonly nearestStoresByArticleId = new Map<string, Store[]>();

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (!changedProperties.has('articleId') && !changedProperties.has('isSizeSelected')) {
      return;
    }
    if (this.isSizeSelected && this.articleId && !this.nearestStoresByArticleId.has(this.articleId)) {
      this.fetchNearestStores(this.articleId);
    }
  }

  private fetchNearestStores(articleId: string) {
    if (this.nearestStoreIds.length === 0) {
      this.nearestStoresByArticleId.set(articleId, []);
      this.requestUpdate();
      return;
    }

    const url = this.modelUrlTemplate.replace('VARIANT_ID', articleId);
    fetch(url)
      .then((response) => (response.ok ? response.json() : { stores: [] }))
      .then((data: { stores?: SizeWithStores[] }) => {
        const inStockStores = data.stores?.find((entry) => entry.article === articleId)?.stores ?? [];
        this.nearestStoresByArticleId.set(articleId, this.rankNearestInStock(inStockStores));
        this.requestUpdate();
      })
      .catch(() => {
        this.nearestStoresByArticleId.set(articleId, []);
        this.requestUpdate();
      });
  }

  private rankNearestInStock(inStockStores: Store[]): Store[] {
    return this.nearestStoreIds
      .map((storeId) => inStockStores.find((store) => store.storeId === storeId))
      .filter((store): store is Store => store !== undefined)
      .slice(0, MAX_NEAREST_STORES);
  }

  protected render(): TemplateResult {
    const stores = this.isSizeSelected ? this.nearestStoresByArticleId.get(this.articleId) : undefined;

    return html`
      <button
        class="ents-or-link bdk-link-primary"
        size="medium"
        aria-haspopup="dialog"
        aria-expanded="false"
        data-test-ents-or-link
        @click="${this.openLayer}"
      >
        ${stores && stores.length > 0 ? this.renderStoresMessage(stores) : this.defaultMessage}
      </button>
    `;
  }

  private renderStoresMessage(stores: Store[]): TemplateResult {
    const names = stores.map((store) => store.name);
    const nameParts = new Intl.ListFormat(document.documentElement.lang || undefined, {
      style: 'long',
      type: 'disjunction',
    }).formatToParts(names);
    const [before, after] = this.storesMessage.split('{0}');
    return html`<span data-test-ents-or-link-message
      >${before}${nameParts.map((part) =>
        part.type === 'element' ? html`<strong data-test-ents-or-link-stores>${part.value}</strong>` : part.value,
      )}${after}</span
    >`;
  }

  private readonly openLayer = () => {
    const detail = { articleId: this.articleId, isSizeSelected: this.isSizeSelected };
    document.dispatchEvent(new CustomEvent('ents:online-reservation:open', { detail }));
    document.dispatchEvent(new CustomEvent('ents:hva-modal:open', { detail }));
    trackClickAndReserveEvent();
  };
}
